// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useMemo, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { apiFetch, describeApiError } from "@/lib/api";

// T-026 — initiative digests admin surface.
// Sends per-topic HTML email digests of shipped work to Marc.
// Backed by /api/admin/initiative-digests/* (backend proxy →
// MCP /tools/digests/*). Service-account bearer
// (BW_BACKEND_MCP_BEARER) must be configured backend-side; if
// not, send/preview/history return 503 with clear instructions.

const INITIATIVES = [
  { value: "auth", group: "Internal" },
  { value: "orchestration", group: "Internal" },
  { value: "bible-governance", group: "Internal" },
  { value: "admin-ui", group: "Internal" },
  { value: "infrastructure", group: "Internal" },
  { value: "compliance", group: "Internal" },
  { value: "playback", group: "Product" },
  { value: "friend", group: "Product" },
  { value: "chat", group: "Product" },
  { value: "user-account", group: "Product" },
] as const;

type DigestPreview = {
  subject: string;
  html_body: string;
  plaintext_body: string;
  task_count: number;
  included_task_ids: string[];
};

type DigestSendResult = {
  digest_id: string;
  task_count: number;
  recipient: string;
  sent_at: string;
  external_message_id: string | null;
  subject: string;
};

type DigestHistoryRow = {
  id: string;
  initiative: string;
  sent_at: string;
  period_start: string;
  period_end: string;
  task_count: number;
  included_task_ids: string[];
  subject: string;
  sent_by: string | null;
};

export function InitiativeDigestsPage() {
  const [initiative, setInitiative] = useState<string>("orchestration");
  const [hoursAgo, setHoursAgo] = useState<number>(24);
  const [narrative, setNarrative] = useState("");
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSend, setLastSend] = useState<DigestSendResult | null>(null);
  const [history, setHistory] = useState<DigestHistoryRow[] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiFetch<DigestHistoryRow[]>("/api/admin/initiative-digests/history?limit=20")
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeApiError(err, "History load failed"));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const computeBody = () => {
    const until = new Date();
    const since = new Date(until.getTime() - hoursAgo * 60 * 60 * 1000);
    return {
      initiative,
      since: since.toISOString(),
      until: until.toISOString(),
      narrative: narrative.trim() || null,
    };
  };

  const doPreview = async () => {
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const p = await apiFetch<DigestPreview>(
        "/api/admin/initiative-digests/preview",
        { method: "POST", body: JSON.stringify(computeBody()) },
      );
      setPreview(p);
    } catch (err) {
      setError(describeApiError(err, "Preview failed"));
    } finally {
      setBusy(false);
    }
  };

  const doSend = async () => {
    if (!confirm(`Send digest for "${initiative}" — emails Marc immediately. Continue?`)) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<DigestSendResult>(
        "/api/admin/initiative-digests/send",
        { method: "POST", body: JSON.stringify(computeBody()) },
      );
      setLastSend(r);
      setPreview(null);
      refresh();
    } catch (err) {
      setError(describeApiError(err, "Send failed"));
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const out: Record<string, typeof INITIATIVES[number][]> = {};
    for (const i of INITIATIVES) {
      (out[i.group] ||= []).push(i);
    }
    return out;
  }, []);

  return (
    <AdminPageShell
      title="Initiative digests"
      description="Per-topic HTML email digests of shipped work. Pick an initiative + window, preview, send. Sent emails recorded in history."
    >
      <div className="space-y-6">
        {error ? (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        ) : null}

        {lastSend ? (
          <AdminCard className="border-emerald-500/40 bg-emerald-500/5">
            <h3 className="font-semibold text-white">
              Sent ✓ — {lastSend.task_count} task{lastSend.task_count === 1 ? "" : "s"}
            </h3>
            <p className="mt-1 text-xs text-emerald-200/80">
              {lastSend.subject}
            </p>
            <p className="mt-2 text-xs text-slate-400">
              To {lastSend.recipient} at {lastSend.sent_at}
              {lastSend.external_message_id
                ? ` · message_id ${lastSend.external_message_id}`
                : ""}
            </p>
          </AdminCard>
        ) : null}

        <AdminCard className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Send digest
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-500">
                Initiative
              </span>
              <select
                value={initiative}
                onChange={(e) => setInitiative(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white"
              >
                {Object.entries(grouped).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((i) => (
                      <option key={i.value} value={i.value}>
                        {i.value}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-500">
                Window (hours back from now)
              </span>
              <input
                type="number"
                min={1}
                max={2160}
                value={hoursAgo}
                onChange={(e) => setHoursAgo(Math.max(1, Number(e.target.value) || 24))}
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Narrative (optional — short paragraph above the table)
            </span>
            <textarea
              value={narrative}
              rows={3}
              placeholder="e.g., Auth pillar shipped end-to-end: T-019 service accounts, T-020 frontend-worker setup, T-022 self-heal."
              onChange={(e) => setNarrative(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white placeholder-slate-600"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={doPreview}
              disabled={busy}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 disabled:opacity-50"
            >
              {busy ? "…" : "Preview"}
            </button>
            <button
              type="button"
              onClick={doSend}
              disabled={busy}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              Send to Marc
            </button>
          </div>
        </AdminCard>

        {preview ? (
          <AdminCard className="space-y-3">
            <div className="flex items-baseline gap-3">
              <h3 className="font-semibold text-white">Preview</h3>
              <span className="text-xs text-slate-400">
                {preview.task_count} task{preview.task_count === 1 ? "" : "s"} · subject:{" "}
                <code className="text-slate-300">{preview.subject}</code>
              </span>
            </div>
            <div
              className="overflow-x-auto rounded-md border border-slate-800 bg-slate-950 p-3"
              dangerouslySetInnerHTML={{ __html: preview.html_body }}
            />
            <details className="text-xs text-slate-400">
              <summary className="cursor-pointer">Plain-text fallback</summary>
              <pre className="mt-2 whitespace-pre-wrap rounded bg-slate-950 p-2 font-mono text-[11px] text-slate-300">
                {preview.plaintext_body}
              </pre>
            </details>
          </AdminCard>
        ) : null}

        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Recent sends
          </h2>
          <button
            type="button"
            onClick={refresh}
            className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-700 hover:text-white"
          >
            Refresh
          </button>
        </div>

        {history === null ? (
          <AdminCard>
            <p className="text-sm text-slate-400">Loading…</p>
          </AdminCard>
        ) : history.length === 0 ? (
          <AdminCard>
            <p className="text-sm text-slate-400">No digests sent yet.</p>
          </AdminCard>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <AdminCard key={h.id} className="text-sm">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="rounded-md border border-slate-700 bg-slate-900/40 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide text-slate-300">
                    {h.initiative}
                  </span>
                  <span className="text-slate-200">{h.subject}</span>
                  <span className="ml-auto text-xs text-slate-500">
                    {h.task_count} task{h.task_count === 1 ? "" : "s"} · {formatRelativeTime(h.sent_at)}
                    {h.sent_by ? ` · by ${h.sent_by}` : ""}
                  </span>
                </div>
                {h.included_task_ids.length > 0 ? (
                  <div className="mt-1 text-xs text-slate-500 font-mono">
                    {h.included_task_ids.join(", ")}
                  </div>
                ) : null}
              </AdminCard>
            ))}
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}

function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
